import { FormControl, FormLabel, Input, Textarea, Divider, Tag, TagLabel } from '@chakra-ui/react';
import { Project } from '../../../types/project';
import { useTranslation } from 'react-i18next';

interface ProjectInfoProps {
  project: Project;
  isEditing: boolean;
  title: string;
  context: string;
  tasksTotal: number;
  tasksVital: number;
  tasksUrgent: number;
  formatDate: (date: string) => string;
  onTitleChange: (value: string) => void;
  onContextChange: (value: string) => void;
}

export const ProjectInfo: React.FC<ProjectInfoProps> = ({
  project,
  isEditing,
  title,
  context,
  tasksTotal,
  tasksVital,
  tasksUrgent,
  formatDate,
  onTitleChange,
  onContextChange
}) => {
  const { t } = useTranslation(['v0/projects', 'v0/common']);
  
  return (
    <div className="bg-white dark:bg-gray-800 p-4 md:p-6 rounded-xl shadow-sm mb-6 border border-gray-200 dark:border-gray-700">
      {isEditing ? (
        <div className="flex flex-col gap-4">
          <FormControl>
            <FormLabel fontSize="lg" fontWeight="bold">{t('v0/projects:form.titleLabel')}</FormLabel>
            <Input
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
              size="lg"
              fontWeight="bold"
              borderRadius="lg"
            />
          </FormControl>
          
          <FormControl>
            <FormLabel fontSize="lg" fontWeight="bold">{t('v0/projects:form.descriptionLabel')}</FormLabel>
            <Textarea
              value={context}
              onChange={(e) => onContextChange(e.target.value)}
              minH="200px"
              borderRadius="lg"
              placeholder={t('v0/projects:form.descriptionPlaceholder')}
            />
          </FormControl>
        </div>
      ) : (
        <>
          <h1 className="text-xl md:text-2xl font-bold mb-4">{project.title}</h1>
          
          <div className="flex justify-between items-center mb-4 flex-wrap md:flex-nowrap gap-2">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center text-white font-medium text-sm">
                {project.title.charAt(0).toUpperCase()}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('v0/projects:projectInfo.createdAt')}: {formatDate(project.createdAt)}
              </p>
            </div>
            
            <div className="flex gap-2">
              <Tag borderRadius="full" colorScheme="cyan">
                <TagLabel>{tasksTotal} {t('v0/projects:card.taskCount')}</TagLabel>
              </Tag>
              {tasksVital > 0 && (
                <Tag borderRadius="full" colorScheme="red">
                  <TagLabel>{tasksVital} {t('v0/common:common.important')}</TagLabel>
                </Tag>
              )}
              {tasksUrgent > 0 && (
                <Tag borderRadius="full" colorScheme="orange">
                  <TagLabel>{tasksUrgent} {t('v0/common:common.urgent')}</TagLabel>
                </Tag>
              )}
            </div>
          </div>
          
          <Divider mb={4} />
          
          <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300 text-sm md:text-md">
            {project.context || t('v0/projects:projectInfo.noDescription')}
          </p>
        </>
      )}
    </div>
  );
}; 